import {createPortal} from 'react-dom';
import React, {useState, useEffect} from 'react';
import styled from 'styled-components';

import {palette, transition} from '../../lib/styleVariables';
import {useKeyboard} from '../../lib/hooks/useKeyboard';

type onCloseFunction = () => void;

interface OverlayProps {
  open?: boolean;
  onClose?: onCloseFunction;
  children?: React.ReactNode;
  useEsc?: boolean;
  backdropCss?: string;
}

const StyledOverlay = styled.div<{open?: boolean}>`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${props => (props.open ? 1 : 0)};
  pointer-events: ${props => (props.open ? 'visible' : 'none')};
  transition: ${transition.fast};
  z-index: 10;
`;

const StyledBackdrop = styled.div<{backdropCss?: string}>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: ${palette.black.base};
  opacity: 0.8;
  z-index: -1;
  ${props => props.backdropCss};
`;

const OverlayComponent = (props: OverlayProps) => (
  <StyledOverlay open={props.open}>
    <StyledBackdrop backdropCss={props.backdropCss} onClick={props.onClose} />
    {props.children}
  </StyledOverlay>
);

const defaultProps = {useEsc: true};

export const Overlay: React.FC<OverlayProps> = props => {
  const {open, useEsc, onClose} = {...defaultProps, ...props};
  const [hasOpened, setHasOpened] = useState(false);

  useEffect(() => {
    if (open)
      setHasOpened(true);
    document.body.style.overflow = open ? 'hidden' : 'initial';
  }, [open]);

  useKeyboard(
    ({esc}) => {
      if (esc && useEsc && hasOpened) {
        setHasOpened(false);
        if (onClose)
          onClose();
      }
    },
    [hasOpened]
  );

  // willUnMount
  useEffect(
    () => () => {
      document.body.style.overflow = 'initial';
    },
    []
  );

  return hasOpened ? createPortal(<OverlayComponent {...props} />, document.body) : null;
};
